import React from "react";
import { Star } from "lucide-react";

// Editorial social-proof strip designed for the dark cinematic hero.
// Sits between the sub-headline and the CTAs. Uses the brand palette
// (cream text · amber accents · bronze rule) so it reads as an
// intentional, premium element rather than a bolted-on marketing badge.
//
// Props (all optional — the component gracefully degrades):
//   avatars       – string[] of image URLs (recommended 4). Rendered
//                   as overlapping circles with a soft amber ring.
//   rating        – string|number displayed prominently next to stars
//                   (default "4.9"). Kept as a string so the CMS can
//                   edit "4.8" ↔ "5.0" without JS re-parsing.
//   ratingStars   – number 0–5 controlling how many filled stars glow
//                   (default 5). Not intended for half-stars.
//   ratingLabel   – short caption under the stars ("Guest rating").
//   trustText     – primary trust sentence ("+12,000 huéspedes …").
//   scope         – optional CMS scope; when provided every leaf gets
//                   a stable data-cms-role for slot-based editing.
const HeroSocialProof = ({
  avatars = [],
  rating = "4.9",
  ratingStars = 5,
  ratingLabel,
  trustText,
  scope,
}) => {
  const stars = Math.max(0, Math.min(5, Math.round(ratingStars)));
  const showAvatars = Array.isArray(avatars) && avatars.length > 0;

  return (
    <div
      className="mt-7 md:mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4 md:gap-6"
      data-testid="hero-social-proof"
      data-cms-scope={scope || undefined}
    >
      {showAvatars && (
        <div className="flex -space-x-3" data-testid="hero-social-proof-avatars">
          {avatars.slice(0, 5).map((src, i) => (
            <img
              key={`avatar-${i}`}
              src={src}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-10 h-10 md:w-11 md:h-11 rounded-full object-cover ring-2 ring-[#D4A373]/70 shadow-[0_4px_14px_rgba(0,0,0,0.4)]"
              style={{ zIndex: avatars.length - i }}
              data-cms-role={scope ? `avatar.${i}` : undefined}
            />
          ))}
        </div>
      )}

      {/* Amber hairline divider — echoes the editorial rules used in
          hotel deep-dive sections; keeps the layout looking curated. */}
      {showAvatars && (
        <span
          className="hidden sm:block h-10 w-px bg-[#D4A373]/40"
          aria-hidden="true"
        />
      )}

      <div className="flex flex-col leading-tight gap-1.5 sm:gap-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <div className="flex items-center gap-0.5" aria-label={`Rating ${rating} out of 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={`star-${i}`}
                className={`w-3.5 h-3.5 md:w-4 md:h-4 ${
                  i < stars
                    ? "fill-[#D4A373] text-[#D4A373]"
                    : "text-[#FDFBF7]/25"
                }`}
                strokeWidth={1.5}
              />
            ))}
          </div>
          <span
            className="font-serif-x text-lg md:text-xl text-[#FDFBF7]"
            data-cms-role={scope ? "rating" : undefined}
          >
            {rating}
          </span>
          {ratingLabel && (
            <span className="text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#D4A373]/85 sm:ml-2 basis-full sm:basis-auto">
              <span data-cms-role={scope ? "rating-label" : undefined}>{ratingLabel}</span>
            </span>
          )}
        </div>
        {trustText && (
          <p
            className="mt-0 sm:mt-1.5 text-xs md:text-sm text-[#FDFBF7]/80 tracking-wide"
            data-testid="hero-social-proof-trust"
          >
            <span data-cms-role={scope ? "trust-text" : undefined}>{trustText}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default HeroSocialProof;
